import type { User, Permission } from '../types';
import { isAdmin as multiRoleIsAdmin, hasRoleSlug } from './permissions';

/**
 * RBAC Utility Service
 * Centralized permission checking logic that can be used throughout the application.
 *
 * RBAC v2: hasRole and isAdmin resolve exclusively from user.roles[] (via
 * utils/permissions). The permission checks below take the effective
 * permission list explicitly and match resource/action exactly (no wildcards),
 * mirroring RBACContext's basic fallback check.
 */
export class RBACUtils {
  /**
   * Check if a user has a specific role.
   * Checks the slugs of the populated roles[] array only.
   */
  static hasRole(user: User | null, role: string): boolean {
    if (!user) return false;
    return hasRoleSlug(user, role);
  }

  /**
   * Check if user is admin (any role with superPermission '*')
   */
  static isAdmin(user: User | null): boolean {
    return multiRoleIsAdmin(user);
  }

  /**
   * Check if user has a specific permission
   */
  static hasPermission(
    permissions: Permission[],
    user: User | null,
    resource: string,
    action: string,
    scope?: string
  ): boolean {
    // Admin has all permissions
    if (this.isAdmin(user)) {
      return true;
    }

    if (!permissions || permissions.length === 0) {
      return false;
    }

    return permissions.some(permission => {
      const resourceMatch = permission.resource === resource;
      const actionMatch = permission.action === action;
      const scopeMatch = !scope || permission.scope === scope || permission.scope === 'global';
      
      return resourceMatch && actionMatch && scopeMatch;
    });
  }

  /**
   * Check if user has any of the specified permissions
   */
  static hasAnyPermission(
    permissions: Permission[],
    user: User | null,
    permissionList: { resource: string; action: string; scope?: string }[]
  ): boolean {
    return permissionList.some(perm => 
      this.hasPermission(permissions, user, perm.resource, perm.action, perm.scope)
    );
  }

  /**
   * Check if user has all of the specified permissions
   */
  static hasAllPermissions(
    permissions: Permission[],
    user: User | null,
    permissionList: { resource: string; action: string; scope?: string }[]
  ): boolean {
    return permissionList.every(perm => 
      this.hasPermission(permissions, user, perm.resource, perm.action, perm.scope)
    );
  }

  /**
   * Get the permission level for a specific resource and action
   */
  static getPermissionLevel(
    permissions: Permission[],
    user: User | null,
    resource: string,
    action: string
  ): 'none' | 'self' | 'pod' | 'project' | 'client' | 'global' {
    if (this.hasPermission(permissions, user, resource, action, 'global')) return 'global';
    if (this.hasPermission(permissions, user, resource, action, 'client')) return 'client';
    if (this.hasPermission(permissions, user, resource, action, 'project')) return 'project';
    if (this.hasPermission(permissions, user, resource, action, 'pod')) return 'pod';
    if (this.hasPermission(permissions, user, resource, action, 'self')) return 'self';
    return 'none';
  }

  /**
   * Check if user has minimum role level.
   * RBAC v2: Checks the highest role in the user's roles[] array (by hierarchy index).
   */
  static hasMinimumRole(user: User | null, minimumRole: string): boolean {
    const roleHierarchy = [
      'team_member',
      'account_manager',
      'finance',
      'project_manager',
      'pod_lead',
      'operations',
      'admin'
    ];

    const minimumRoleIndex = roleHierarchy.indexOf(minimumRole);

    // RBAC v2: check all roles and use the highest
    if (user?.roles && user.roles.length > 0) {
      const highestIndex = user.roles.reduce((maxIdx, r) => {
        const slug = typeof r === 'object' && r !== null ? r.slug : '';
        const idx = roleHierarchy.indexOf(slug);
        return idx > maxIdx ? idx : maxIdx;
      }, -1);
      return highestIndex >= minimumRoleIndex;
    }

    // Fallback: use first role slug
    const userRoleIndex = roleHierarchy.indexOf(user?.roles?.[0]?.slug || '');
    return userRoleIndex >= minimumRoleIndex;
  }

  /**
   * Check if user can perform an action with detailed response
   */
  static canPerformAction(
    permissions: Permission[],
    user: User | null,
    resource: string,
    action: string,
    scope?: string
  ): { allowed: boolean; reason?: string } {
    if (this.isAdmin(user)) {
      return { allowed: true };
    }

    if (!user) {
      return { allowed: false, reason: 'User not authenticated' };
    }

    const hasPermission = this.hasPermission(permissions, user, resource, action, scope);
    
    if (!hasPermission) {
      return { 
        allowed: false, 
        reason: `Insufficient permissions for ${action} on ${resource}${scope ? ` (${scope})` : ''}` 
      };
    }

    return { allowed: true };
  }

  /**
   * Filter permissions by resource
   */
  static getPermissionsForResource(permissions: Permission[], resource: string): Permission[] {
    return permissions.filter(permission => permission.resource === resource);
  }

  /**
   * Get all available actions for a resource
   */
  static getAvailableActions(permissions: Permission[], resource: string): string[] {
    const resourcePermissions = this.getPermissionsForResource(permissions, resource);
    return [...new Set(resourcePermissions.map(p => p.action))];
  }

  /**
   * Get all available scopes for a resource and action
   */
  static getAvailableScopes(permissions: Permission[], resource: string, action: string): string[] {
    const filteredPermissions = permissions.filter(p => 
      p.resource === resource && p.action === action
    );
    return [...new Set(filteredPermissions.map(p => p.scope))];
  }

  /**
   * Check if user can access a specific resource ID based on scope
   */
  static canAccessResource(
    permissions: Permission[],
    user: User | null,
    resource: string,
    action: string,
    resourceId?: string,
    context?: {
      podId?: string;
      projectId?: string;
      clientId?: string;
    }
  ): boolean {
    // Admin can access everything
    if (this.isAdmin(user)) {
      return true;
    }

    // Check global permission first
    if (this.hasPermission(permissions, user, resource, action, 'global')) {
      return true;
    }

    // Check scope-specific permissions
    if (context?.clientId && this.hasPermission(permissions, user, resource, action, 'client')) {
      return user?.assignedClients?.includes(context.clientId) || false;
    }

    if (context?.projectId && this.hasPermission(permissions, user, resource, action, 'project')) {
      return user?.assignedProjects?.includes(context.projectId) || false;
    }

    if (context?.podId && this.hasPermission(permissions, user, resource, action, 'pod')) {
      return user?.assignedPods?.includes(context.podId) || false;
    }

    // Check self permission
    if (resourceId && this.hasPermission(permissions, user, resource, action, 'self')) {
      return resourceId === user?.id;
    }

    return false;
  }

  /**
   * Get user's effective permissions summary
   */
  static getPermissionsSummary(permissions: Permission[], user: User | null): {
    totalPermissions: number;
    resourceCount: number;
    actionCount: number;
    scopeCount: number;
    isAdmin: boolean;
    role: string;
    resources: string[];
    actions: string[];
    scopes: string[];
  } {
    const resources = [...new Set(permissions.map(p => p.resource))];
    const actions = [...new Set(permissions.map(p => p.action))];
    const scopes = [...new Set(permissions.map(p => p.scope))];

    return {
      totalPermissions: permissions.length,
      resourceCount: resources.length,
      actionCount: actions.length,
      scopeCount: scopes.length,
      isAdmin: this.isAdmin(user),
      role: user?.roles?.[0]?.slug || 'none',
      resources,
      actions,
      scopes,
    };
  }

  /**
   * Validate permission structure
   */
  static validatePermission(permission: Permission): boolean {
    return !!(
      permission.resource &&
      permission.action &&
      permission.scope &&
      typeof permission.resource === 'string' &&
      typeof permission.action === 'string' &&
      typeof permission.scope === 'string'
    );
  }

  /**
   * Validate permissions array
   */
  static validatePermissions(permissions: Permission[]): {
    valid: boolean;
    invalidPermissions: Permission[];
    errors: string[];
  } {
    const invalidPermissions: Permission[] = [];
    const errors: string[] = [];

    permissions.forEach((permission, index) => {
      if (!this.validatePermission(permission)) {
        invalidPermissions.push(permission);
        errors.push(`Permission at index ${index} is invalid`);
      }
    });

    return {
      valid: invalidPermissions.length === 0,
      invalidPermissions,
      errors,
    };
  }
}

// Export commonly used permission constants
export const PERMISSION_SCOPES = {
  GLOBAL: 'global',
  POD: 'pod',
  PROJECT: 'project',
  CLIENT: 'client',
  SELF: 'self',
  TEAM: 'team'
} as const;

export const COMMON_RESOURCES = {
  PODS: 'pods',
  PROJECTS: 'projects',
  CLIENTS: 'clients',
  RETAINERS: 'retainers',
  USERS: 'users',
  REPORTS: 'reports',
  SYSTEM: 'system'
} as const;

export const COMMON_ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  EDIT: 'edit',
  DELETE: 'delete',
  MANAGE: 'manage',
  ASSIGN: 'assign',
  APPROVE: 'approve',
  EXPORT: 'export'
} as const;

export default RBACUtils;
